import { WIZARD_STEPS } from "@/types/wizard";
import { motion } from "framer-motion";

interface WizardProgressProps {
  currentStep: number;
  isDark: boolean;
}

const WizardProgress = ({ currentStep, isDark }: WizardProgressProps) => {
  const progressPercent = Math.round(((currentStep - 1) / (WIZARD_STEPS.length - 1)) * 100);
  const currentStepTitle = WIZARD_STEPS[currentStep - 1]?.title || "";
  
  return (
    <div className="mb-10">
      {/* Modern Progress Container */}
      <div 
        className="relative p-6 rounded-2xl"
        style={{
          backgroundColor: isDark ? "#0f0f0f" : "#f8f8f8",
          boxShadow: isDark
            ? "inset 4px 4px 10px #050505, inset -4px -4px 10px #151515"
            : "inset 4px 4px 10px #e8e8e8, inset -4px -4px 10px #ffffff",
        }}
      >
        {/* Header with percentage and step title */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <motion.div
              key={progressPercent}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="text-3xl md:text-4xl font-bold"
              style={{ color: "#E4683B" }}
            >
              {progressPercent}%
            </motion.div>
            <span 
              className="text-sm md:text-base font-medium opacity-60"
              style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}
            >
              Complete
            </span>
          </div>
          <div 
            className="text-right"
            style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}
          >
            <span className="text-sm opacity-50">Step {currentStep} of {WIZARD_STEPS.length}</span>
            <p className="text-sm md:text-base font-semibold" style={{ color: "#E4683B" }}>
              {currentStepTitle}
            </p>
          </div>
        </div>

        {/* 3D Progress Bar */}
        <div 
          className="h-3 rounded-full overflow-hidden relative"
          style={{
            backgroundColor: isDark ? "#1a1a1a" : "#e0e0e0",
            boxShadow: isDark
              ? "inset 2px 2px 4px #000000"
              : "inset 2px 2px 4px #c0c0c0",
          }}
        >
          <motion.div 
            className="h-full rounded-full relative"
            initial={{ width: 0 }}
            animate={{ width: `${progressPercent}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            style={{
              background: "linear-gradient(90deg, #E4683B 0%, #f08a5d 50%, #E4683B 100%)",
              boxShadow: "0 2px 8px rgba(228, 104, 59, 0.4), inset 0 1px 0 rgba(255,255,255,0.3)",
            }}
          >
            {/* Shine effect */}
            <div 
              className="absolute inset-0 rounded-full"
              style={{
                background: "linear-gradient(180deg, rgba(255,255,255,0.3) 0%, transparent 50%)",
              }}
            />
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default WizardProgress;
